import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoginForm from '../components/auth/LoginForm';
import RegisterForm from '../components/auth/RegisterForm';
import LanguageSwitcher from '../components/layout/LanguageSwitcher';
import AppBrandInline from '../components/layout/AppBrandInline';
import { useSupabaseBackendStatus } from '../hooks/useSupabaseBackendStatus';
import './LandingPage.css';

function LandingPage({ onAuthSuccess }) {
  const { t, i18n } = useTranslation(['landing', 'shared']);
  const { backendOnline } = useSupabaseBackendStatus();
  const [isLogin, setIsLogin] = useState(true);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  /** When set (e.g. in `.env`), shows the primary checkout CTA; otherwise a friendly notice is shown. */
  const earlyAccessUrl = process.env.REACT_APP_EARLY_ACCESS_URL?.trim();
  const googlePlayUrl = process.env.REACT_APP_GOOGLE_PLAY_URL?.trim() || '';
  const appStoreUrl = process.env.REACT_APP_APP_STORE_URL?.trim() || '';
  const basePublic = process.env.PUBLIC_URL || '';
  const googlePlayBadgeSrc = `${basePublic}/store-badges/${
    i18n.language?.startsWith('pl') ? 'google-play-badge-pl.png' : 'google-play-badge.png'
  }`;
  const appStoreBadgeSrc = `${basePublic}/store-badges/app-store-badge.svg`;
  const privacyPolicyUrl = process.env.REACT_APP_PRIVACY_POLICY_URL?.trim() || '';
  const termsOfServiceUrl = process.env.REACT_APP_TERMS_URL?.trim() || '';
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL?.trim() || '';
  const contactPageUrl = process.env.REACT_APP_CONTACT_URL?.trim() || '';
  const contactHref =
    contactPageUrl || (supportEmail ? `mailto:${supportEmail}` : '');

  const handleRegisterSuccess = () => {
    setRegisterSuccess(true);
    setTimeout(() => {
      setIsLogin(true);
      setRegisterSuccess(false);
    }, 3000);
  };

  const scrollPresaleSupportToAccount = () => {
    document.getElementById('lp-account')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const faviconSrc = `${process.env.PUBLIC_URL}/favicon.ico`;

  return (
    <div className="lp">
      <div className="lp-orbit-bg" aria-hidden="true">
        <div className="lp-orbit-bg-inner">
          <div className="lp-orbit-circle lp-orbit-circle--small" />
          <div className="lp-orbit-circle lp-orbit-circle--medium" />
          <div className="lp-orbit-circle lp-orbit-circle--large" />
          <div className="lp-orbit-circle lp-orbit-circle--xlarge" />
          <div className="lp-orbit-circle lp-orbit-circle--xxlarge" />
        </div>
      </div>
      <header className="lp-topbar">
        <div className="lp-topbar-inner">
          <div className="lp-topbar-brand">
            <AppBrandInline />
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="lp-main" id="main-content">
        <section className="lp-hero" aria-labelledby="lp-hero-title">
          <div className="lp-shell">
            <div className="lp-hero-panel">
              <div className="lp-hero-art">
                <img
                  className="lp-hero-img"
                  src={`${process.env.PUBLIC_URL}/landing-hero-plangonow.png`}
                  alt={t('landing.hero.imageAlt')}
                  width={1200}
                  height={675}
                  loading="eager"
                  fetchPriority="high"
                />
                <div className="lp-hero-art-veil" aria-hidden>
                  <svg
                    className="lp-hero-art-veil-svg"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <defs>
                      <pattern
                        id="lp-hero-veil-grid"
                        width="8"
                        height="8"
                        patternUnits="userSpaceOnUse"
                      >
                        <path
                          d="M 8 0 L 0 0 0 8"
                          fill="none"
                          stroke="rgba(255,255,255,0.22)"
                          strokeWidth="0.35"
                        />
                      </pattern>
                    </defs>
                    <rect width="100" height="100" fill="url(#lp-hero-veil-grid)" />
                  </svg>
                </div>
                <div className="lp-hero-art-motifs" aria-hidden>
                  <svg
                    className="lp-hero-art-motifs-svg"
                    viewBox="0 0 1200 675"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="xMidYMid slice"
                    aria-hidden
                  >
                    <defs>
                      <linearGradient id="lp-hero-motif-fill" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#feb139" />
                        <stop offset="100%" stopColor="#fd7651" />
                      </linearGradient>
                      <linearGradient id="lp-hero-motif-line" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.12)" />
                        <stop offset="50%" stopColor="rgba(254,177,57,0.5)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0.15)" />
                      </linearGradient>
                    </defs>
                    {/* Rozproszone drobne akcenty (pod klastrami - nie przykrywaja hover) */}
                    <g className="lp-hero-motif-scatter" pointerEvents="none" opacity="0.72">
                      <circle cx="328" cy="112" r="2.5" fill="rgba(254,177,57,0.5)" />
                      <circle cx="1048" cy="372" r="2" fill="rgba(255,255,255,0.28)" />
                      <circle cx="172" cy="352" r="3" fill="rgba(253,118,81,0.35)" />
                      <circle cx="768" cy="528" r="2" fill="rgba(255,255,255,0.22)" />
                      <circle cx="592" cy="88" r="2.5" fill="rgba(254,177,57,0.4)" />
                      <circle cx="438" cy="556" r="2" fill="rgba(255,255,255,0.2)" />
                      <circle cx="988" cy="124" r="2.8" fill="rgba(253,118,81,0.32)" />
                      <circle cx="216" cy="188" r="2" fill="rgba(255,255,255,0.25)" />
                      <circle cx="682" cy="198" r="2.2" fill="rgba(254,177,57,0.38)" />
                      <circle cx="1120" cy="268" r="2" fill="rgba(255,255,255,0.18)" />
                      <path
                        d="M 388 132 L 468 154"
                        fill="none"
                        stroke="rgba(255,255,255,0.18)"
                        strokeWidth="1"
                        strokeDasharray="4 6"
                        strokeLinecap="round"
                      />
                      <path
                        d="M 960 480 L 1048 452"
                        fill="none"
                        stroke="rgba(254,177,57,0.22)"
                        strokeWidth="0.9"
                        strokeDasharray="3 5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M 520 72 L 520 96 M 508 84 L 532 84"
                        fill="none"
                        stroke="rgba(255,255,255,0.2)"
                        strokeWidth="1"
                        strokeLinecap="round"
                      />
                      <path
                        d="M 1088 420 L 1088 446 M 1076 433 L 1100 433"
                        fill="none"
                        stroke="rgba(253,118,81,0.18)"
                        strokeWidth="0.9"
                        strokeLinecap="round"
                      />
                      <path
                        d="M 352 288 l 16 0 l -8 -11 Z"
                        fill="rgba(255,255,255,0.16)"
                        transform="rotate(12 360 282)"
                      />
                      <path
                        d="M 812 388 l 0 14 l 11 -7 Z"
                        fill="rgba(254,177,57,0.22)"
                      />
                      <path
                        d="M 228 468 l 12 0 l -6 -9 Z"
                        fill="rgba(255,255,255,0.14)"
                        transform="rotate(-28 234 462)"
                      />
                      <path
                        d="M 902 520 l 10 0 l -5 -8 Z"
                        fill="rgba(253,118,81,0.2)"
                        transform="rotate(140 907 514)"
                      />
                      <g transform="translate(708 138) scale(0.34)" opacity="0.55">
                        <path
                          d="M32 62C32 62 4 40 4 22C4 10 14 0 32 0C50 0 60 10 60 22C60 40 32 62 32 62Z"
                          fill="url(#lp-hero-motif-fill)"
                          stroke="rgba(255,255,255,0.25)"
                          strokeWidth="1.2"
                        />
                      </g>
                      <g transform="translate(528 188) scale(0.3)" opacity="0.45">
                        <path
                          d="M32 62C32 62 4 40 4 22C4 10 14 0 32 0C50 0 60 10 60 22C60 40 32 62 32 62Z"
                          fill="none"
                          stroke="rgba(255,255,255,0.35)"
                          strokeWidth="1.8"
                        />
                      </g>
                      <g transform="translate(1018 292) scale(0.28)" opacity="0.5">
                        <path
                          d="M32 62C32 62 4 40 4 22C4 10 14 0 32 0C50 0 60 10 60 22C60 40 32 62 32 62Z"
                          fill="url(#lp-hero-motif-fill)"
                          stroke="rgba(255,255,255,0.2)"
                          strokeWidth="1.1"
                        />
                      </g>
                      <g transform="translate(1180 180) scale(0.26)" opacity="0.42">
                        <path
                          d="M32 62C32 62 4 40 4 22C4 10 14 0 32 0C50 0 60 10 60 22C60 40 32 62 32 62Z"
                          fill="none"
                          stroke="rgba(254,177,57,0.45)"
                          strokeWidth="1.6"
                        />
                      </g>
                      <circle cx="860" cy="420" r="4" fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="0.9" />
                      <circle cx="296" cy="498" r="5" fill="none" stroke="rgba(254,177,57,0.2)" strokeWidth="0.75" />
                    </g>
                    {/* Kalendarz - kontur + jedna kreska */}
                    <g transform="translate(92 142)">
                      <g className="lp-hero-motif-cluster">
                        <rect width="64" height="72" rx="10" fill="transparent" />
                        <rect
                          width="64"
                          height="72"
                          rx="10"
                          fill="none"
                          stroke="rgba(255,255,255,0.28)"
                          strokeWidth="1.25"
                        />
                        <line
                          x1="18"
                          y1="26"
                          x2="46"
                          y2="26"
                          stroke="url(#lp-hero-motif-fill)"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          opacity="0.75"
                        />
                      </g>
                    </g>
                    {/* Trasa - jedna krzywa, trzy punkty (szeroki hit pod hover) */}
                    <g className="lp-hero-motif-cluster lp-hero-motif-cluster--route">
                      <path
                        d="M 248 432 Q 455 175 640 292 T 902 236"
                        fill="none"
                        stroke="url(#lp-hero-motif-line)"
                        strokeWidth="1.35"
                        strokeLinecap="round"
                        opacity="0.65"
                      />
                      <circle cx="248" cy="432" r="5.5" fill="url(#lp-hero-motif-fill)" opacity="0.78" />
                      <circle cx="640" cy="292" r="5" fill="url(#lp-hero-motif-fill)" opacity="0.72" />
                      <circle cx="902" cy="236" r="6" fill="url(#lp-hero-motif-fill)" opacity="0.82" />
                      <path
                        d="M 248 432 Q 455 175 640 292 T 902 236"
                        fill="none"
                        stroke="transparent"
                        strokeWidth="22"
                        strokeLinecap="round"
                      />
                    </g>
                    {/* Pinezka */}
                    <g transform="translate(878 72) scale(1.85)">
                      <g className="lp-hero-motif-cluster">
                        <rect x="-8" y="-6" width="76" height="76" rx="12" fill="transparent" />
                        <path
                          d="M32 62C32 62 4 40 4 22C4 10 14 0 32 0C50 0 60 10 60 22C60 40 32 62 32 62Z"
                          fill="url(#lp-hero-motif-fill)"
                          stroke="rgba(255,255,255,0.22)"
                          strokeWidth="0.9"
                          opacity="0.88"
                        />
                        <circle cx="32" cy="22" r="7" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.8" />
                      </g>
                    </g>
                    {/* Dwie sylwetki - same kola */}
                    <g transform="translate(138 524)">
                      <g className="lp-hero-motif-cluster">
                        <rect x="-36" y="-18" width="72" height="36" rx="10" fill="transparent" />
                        <circle cx="-18" cy="0" r="10" fill="none" stroke="rgba(255,255,255,0.26)" strokeWidth="1.1" />
                        <circle cx="18" cy="-3" r="9" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.1" />
                      </g>
                    </g>
                  </svg>
                </div>
                <div className="lp-hero-art-scrim" aria-hidden />
              </div>
              <div className="lp-hero-copy">
                <p className="lp-hero-eyebrow">{t('landing.hero.eyebrow')}</p>
                <h1 id="lp-hero-title" className="lp-hero-title">
                  {t('landing.title')}
                </h1>
                <p className="lp-hero-lead">{t('landing.subtitle')}</p>
                <div className="lp-hero-beta">
                  <span className="lp-chip">{t('landing.beta.badge')}</span>
                  <p className="lp-hero-beta-text">{t('landing.beta.message')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div
          className="lp-trust-strip"
          role="region"
          aria-label={t('landing.trustStrip.aria')}
        >
          <div className="lp-shell">
            <ul className="lp-trust-list">
              <li className="lp-trust-item">{t('landing.trustStrip.item1')}</li>
              <li className="lp-trust-item">{t('landing.trustStrip.item2')}</li>
              <li className="lp-trust-item">{t('landing.trustStrip.item3')}</li>
            </ul>
          </div>
        </div>

        <div className="lp-shell lp-columns">
          <div className="lp-column-primary">
            <section className="lp-features" aria-labelledby="lp-features-heading">
              <div className="lp-features-head">
                <span className="lp-features-head-mark" aria-hidden="true" />
                <h2 id="lp-features-heading" className="lp-section-title">
                  {t('landing.featuresHeading')}
                </h2>
              </div>
              <ul className="lp-feature-showcase">
                <li className="lp-feature-card lp-feature-card--lead">
                  <div className="lp-feature-icon-wrap">
                    <img
                      className="lp-feature-icon-img"
                      src={`${process.env.PUBLIC_URL}/landing-icons/feature-small-groups.svg`}
                      alt={t('landing.feature1.iconAlt')}
                      width={64}
                      height={64}
                      decoding="async"
                    />
                  </div>
                  <div className="lp-feature-body">
                    <h3 className="lp-feature-title">{t('landing.feature1.title')}</h3>
                    <p className="lp-feature-desc">{t('landing.feature1.description')}</p>
                  </div>
                </li>
                <li className="lp-feature-card lp-feature-card--tile">
                  <div className="lp-feature-icon-wrap">
                    <img
                      className="lp-feature-icon-img"
                      src={`${process.env.PUBLIC_URL}/landing-icons/feature-interests.svg`}
                      alt={t('landing.feature2.iconAlt')}
                      width={56}
                      height={56}
                      decoding="async"
                    />
                  </div>
                  <div className="lp-feature-body">
                    <h3 className="lp-feature-title">{t('landing.feature2.title')}</h3>
                    <p className="lp-feature-desc">{t('landing.feature2.description')}</p>
                  </div>
                </li>
                <li className="lp-feature-card lp-feature-card--tile">
                  <div className="lp-feature-icon-wrap">
                    <img
                      className="lp-feature-icon-img"
                      src={`${process.env.PUBLIC_URL}/landing-icons/feature-local.svg`}
                      alt={t('landing.feature3.iconAlt')}
                      width={56}
                      height={56}
                      decoding="async"
                    />
                  </div>
                  <div className="lp-feature-body">
                    <h3 className="lp-feature-title">{t('landing.feature3.title')}</h3>
                    <p className="lp-feature-desc">{t('landing.feature3.description')}</p>
                  </div>
                </li>
              </ul>
            </section>

            <section className="lp-how" aria-labelledby="lp-how-heading">
              <div className="lp-features-head">
                <span className="lp-features-head-mark" aria-hidden />
                <h2 id="lp-how-heading" className="lp-section-title">
                  {t('landing.howItWorks.heading')}
                </h2>
              </div>
              <ol className="lp-how-steps">
                <li className="lp-how-step">
                  <span className="lp-how-num" aria-hidden>
                    1
                  </span>
                  <div className="lp-how-body">
                    <h3 className="lp-how-title">{t('landing.howItWorks.step1Title')}</h3>
                    <p className="lp-how-desc">{t('landing.howItWorks.step1Desc')}</p>
                  </div>
                </li>
                <li className="lp-how-step">
                  <span className="lp-how-num" aria-hidden>
                    2
                  </span>
                  <div className="lp-how-body">
                    <h3 className="lp-how-title">{t('landing.howItWorks.step2Title')}</h3>
                    <p className="lp-how-desc">{t('landing.howItWorks.step2Desc')}</p>
                  </div>
                </li>
                <li className="lp-how-step">
                  <span className="lp-how-num" aria-hidden>
                    3
                  </span>
                  <div className="lp-how-body">
                    <h3 className="lp-how-title">{t('landing.howItWorks.step3Title')}</h3>
                    <p className="lp-how-desc">{t('landing.howItWorks.step3Desc')}</p>
                  </div>
                </li>
              </ol>
            </section>

            <section className="lp-presale-section" aria-labelledby="lp-presale-heading">
              <div className="lp-features-head">
                <span className="lp-features-head-mark" aria-hidden="true" />
                <h2 id="lp-presale-heading" className="lp-section-title">
                  {t('landing.earlyAccess.title')}
                </h2>
              </div>
              <div className="lp-presale">
                <div className="lp-presale-inner">
                  <span className="lp-presale-eyebrow">{t('landing.earlyAccess.eyebrow')}</span>
                  <p className="lp-presale-lead">{t('landing.earlyAccess.lead')}</p>
                <div className="lp-presale-price">
                  <span className="lp-presale-price-label">{t('landing.earlyAccess.priceLabel')}</span>
                  <span className="lp-presale-price-value">{t('landing.earlyAccess.price')}</span>
                  <span className="lp-presale-price-note">{t('landing.earlyAccess.priceNote')}</span>
                </div>
                <ul className="lp-presale-list">
                  <li>{t('landing.earlyAccess.point1')}</li>
                  <li>{t('landing.earlyAccess.point2')}</li>
                  <li>{t('landing.earlyAccess.point3')}</li>
                  <li>{t('landing.earlyAccess.point4')}</li>
                </ul>
                {earlyAccessUrl ? (
                  <a
                    className="lp-btn-primary"
                    href={earlyAccessUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('landing.earlyAccess.cta')}
                  </a>
                ) : (
                  <p className="lp-presale-hint">{t('landing.earlyAccess.ctaHint')}</p>
                )}
                  {earlyAccessUrl ? (
                    <a
                      href={earlyAccessUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="lp-presale-support-fab"
                      aria-label={t('landing.earlyAccess.supportFabAriaCheckout')}
                    >
                      <img src={faviconSrc} alt="" width="22" height="22" decoding="async" aria-hidden />
                      <span>{t('landing.earlyAccess.eyebrow')}</span>
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="lp-presale-support-fab"
                      onClick={scrollPresaleSupportToAccount}
                      aria-label={t('landing.earlyAccess.supportFabAriaAccount')}
                    >
                      <img src={faviconSrc} alt="" width="22" height="22" decoding="async" aria-hidden />
                      <span>{t('landing.earlyAccess.eyebrow')}</span>
                    </button>
                  )}
                </div>
              </div>
            </section>
          </div>

          <aside className="lp-column-aside" id="lp-account" aria-labelledby="lp-auth-heading">
            <div className="lp-app-stores">
              <h2 id="lp-auth-heading" className="lp-app-stores-title">
                {isLogin
                  ? t('landing.authPanelHeadingLogin')
                  : t('landing.authPanelHeadingRegister')}
              </h2>
              <p className="lp-app-stores-lead">{t('landing.authIntro')}</p>
            </div>
            <div className="lp-auth-surface">
              {registerSuccess && (
                <div className="lp-auth-success">{t('auth.messages.registrationComplete')}</div>
              )}
              {isLogin ? (
                <LoginForm onLoginSuccess={onAuthSuccess} hideHeading />
              ) : (
                <RegisterForm onRegisterSuccess={handleRegisterSuccess} hideHeading />
              )}
              <div className="lp-auth-switch">
                {isLogin ? (
                  <p>
                    {t('auth.switch.noAccount')}{' '}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setIsLogin(false)}
                    >
                      {t('auth.switch.register')}
                    </button>
                  </p>
                ) : (
                  <p>
                    {t('auth.switch.haveAccount')}{' '}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setIsLogin(true)}
                    >
                      {t('auth.switch.login')}
                    </button>
                  </p>
                )}
              </div>
            </div>

            <div className="lp-app-stores" aria-labelledby="lp-app-stores-heading">
              <h3 id="lp-app-stores-heading" className="lp-app-stores-title">
                {t('landing.appStores.title')}
              </h3>
              <p className="lp-app-stores-lead">{t('landing.appStores.lead')}</p>
              <div className="lp-app-stores-badges">
                {googlePlayUrl ? (
                  <a
                    className="lp-store-badge-link"
                    href={googlePlayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('landing.appStores.googleAria')}
                  >
                    <img
                      className="lp-store-badge-img"
                      src={googlePlayBadgeSrc}
                      alt=""
                      width={646}
                      height={250}
                      decoding="async"
                    />
                  </a>
                ) : (
                  <span
                    className="lp-store-badge-link is-disabled"
                    title={t('landing.appStores.soonHint')}
                    aria-label={t('landing.appStores.googleSoonAria')}
                  >
                    <img
                      className="lp-store-badge-img"
                      src={googlePlayBadgeSrc}
                      alt=""
                      width={646}
                      height={250}
                      decoding="async"
                    />
                  </span>
                )}
                {appStoreUrl ? (
                  <a
                    className="lp-store-badge-link"
                    href={appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('landing.appStores.appleAria')}
                  >
                    <img
                      className="lp-store-badge-img"
                      src={appStoreBadgeSrc}
                      alt=""
                      width={120}
                      height={40}
                      decoding="async"
                    />
                  </a>
                ) : (
                  <span
                    className="lp-store-badge-link is-disabled"
                    title={t('landing.appStores.soonHint')}
                    aria-label={t('landing.appStores.appleSoonAria')}
                  >
                    <img
                      className="lp-store-badge-img"
                      src={appStoreBadgeSrc}
                      alt=""
                      width={120}
                      height={40}
                      decoding="async"
                    />
                  </span>
                )}
              </div>
            </div>
          </aside>
        </div>

        <section className="lp-quote-band" aria-labelledby="lp-quote-heading">
          <div className="lp-shell">
            <h2 id="lp-quote-heading" className="lp-visually-hidden">
              {t('landing.quote.headingHidden')}
            </h2>
            <blockquote className="lp-quote">
              <p>{t('landing.quote.text')}</p>
              <footer className="lp-quote-by">{t('landing.quote.by')}</footer>
            </blockquote>
          </div>
        </section>

        <section className="lp-cta-wide" aria-labelledby="lp-cta-wide-heading">
          <div className="lp-shell lp-cta-wide-inner">
            <div className="lp-cta-wide-copy">
              <h2 id="lp-cta-wide-heading" className="lp-cta-wide-title">
                {t('landing.ctaWide.title')}
              </h2>
              <p className="lp-cta-wide-lead">{t('landing.ctaWide.lead')}</p>
            </div>
            <a className="lp-btn-primary lp-cta-wide-btn" href="#lp-account">
              {t('landing.ctaWide.button')}
            </a>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div
          className="lp-footer-status"
          role="status"
          aria-live="polite"
          aria-label={t('landing.appStatusAria')}
        >
          <span
            className={`lp-status-dot ${
              backendOnline === true
                ? 'lp-status-dot--online'
                : backendOnline === false
                  ? 'lp-status-dot--offline'
                  : 'lp-status-dot--pending'
            }`}
            aria-hidden
          />
          <span>
            {t('landing.appStatusLabel')}:{' '}
            {backendOnline === null
              ? t('landing.appStatusChecking')
              : backendOnline
                ? t('landing.appStatusOnline')
                : t('landing.appStatusOffline')}
          </span>
        </div>
        <nav className="lp-footer-nav" aria-label={t('landing.footerNavAria')}>
          {privacyPolicyUrl ? (
            <a
              className="lp-footer-legal-link"
              href={privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('landing.footerLegal.privacy')}
            </a>
          ) : (
            <span
              className="lp-footer-legal-link is-disabled"
              title={t('landing.footerLegal.soonHint')}
            >
              {t('landing.footerLegal.privacy')}
            </span>
          )}
          <span className="lp-footer-nav-sep" aria-hidden>
            ·
          </span>
          {termsOfServiceUrl ? (
            <a
              className="lp-footer-legal-link"
              href={termsOfServiceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('landing.footerLegal.terms')}
            </a>
          ) : (
            <span
              className="lp-footer-legal-link is-disabled"
              title={t('landing.footerLegal.soonHint')}
            >
              {t('landing.footerLegal.terms')}
            </span>
          )}
          <span className="lp-footer-nav-sep" aria-hidden>
            ·
          </span>
          {contactHref ? (
            <a
              className="lp-footer-legal-link"
              href={contactHref}
              {...(contactHref.startsWith('http')
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
            >
              {t('landing.footerLegal.contact')}
            </a>
          ) : (
            <span
              className="lp-footer-legal-link is-disabled"
              title={t('landing.footerLegal.soonHint')}
            >
              {t('landing.footerLegal.contact')}
            </span>
          )}
        </nav>
        <p className="lp-footer-copy">{t('landing.footer')}</p>
      </footer>
    </div>
  );
}

export default LandingPage;
